import { ReloadOutlined } from '@ant-design/icons';
import type { BudgetUsageRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../session';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const yearOptions = (): { label: string; value: number }[] => {
  const current = dayjs().year();
  return Array.from({ length: 5 }, (_, index) => {
    const year = current - index;
    return { label: `${year} 年度`, value: year };
  });
};

/**
 * 预算余额 + 预算使用流水：
 * - 余额看「额度 / 已用 / 冻结 / 可用 / 执行率」，可按科室下钻；
 * - 流水看每次占用/释放/冲销的金额与执行率快照，支持只看超预算记录。
 */
export default function BudgetOps() {
  const [budgetYear, setBudgetYear] = useState<number>(dayjs().year());
  const [deptId, setDeptId] = useState<string>('');
  const [overBudgetOnly, setOverBudgetOnly] = useState(false);

  const balance = useQuery({
    queryKey: ['ops', 'budget', 'balance', deptId, budgetYear],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.budget.balance({ deptId: deptId || undefined, budgetYear })
  });

  const usage = useInfiniteQuery({
    queryKey: ['ops', 'budget', 'usage', deptId, budgetYear, overBudgetOnly],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.billing.budget.usage({
        lastId: pageParam,
        size: PAGE_SIZE,
        deptId: deptId || undefined,
        budgetYear,
        overBudgetOnly: overBudgetOnly || undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = usage.data?.pages.flatMap((page) => page.items) ?? [];
  const summary = balance.data;

  const columns: ColumnsType<BudgetUsageRow> = [
    { title: '发生时间', dataIndex: 'bizDate', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    { title: '科室', dataIndex: 'deptName', width: 160, render: (value: string | null) => value || '-' },
    { title: '期间', dataIndex: 'periodNo', width: 80, align: 'right', render: (value: number) => `${value} 期` },
    { title: '业务类型', dataIndex: 'bizType', width: 130, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 190, render: (value: string | null) => value || '-' },
    {
      title: '使用类型',
      dataIndex: 'usageType',
      width: 110,
      render: (value: string | null) => {
        const color = value === 'RELEASE' || value === 'ROLLBACK' ? 'green' : value === 'FREEZE' ? 'orange' : 'blue';
        return <Tag color={color}>{value || '-'}</Tag>;
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    { title: '前余额', dataIndex: 'beforeUsed', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '后余额', dataIndex: 'afterUsed', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '执行率',
      dataIndex: 'execRateSnapshot',
      width: 100,
      align: 'right',
      render: (value: number) => `${formatQty((value ?? 0) * 100)}%`
    },
    {
      title: '超预算',
      dataIndex: 'overBudgetFlag',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="red">是</Tag> : '-')
    }
  ];

  return (
    <Card
      className="page-card"
      title="预算管理"
      extra={
        <Space wrap>
          <Select
            value={budgetYear}
            style={{ width: 130 }}
            options={yearOptions()}
            onChange={setBudgetYear}
          />
          <Select
            value={deptId}
            style={{ width: 170 }}
            showSearch
            optionFilterProp="label"
            onChange={setDeptId}
            options={[
              { value: '', label: '全机构' },
              ...(summary?.depts ?? []).map((row) => ({ value: row.deptId, label: row.deptName ?? row.deptId }))
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={balance.isFetching || usage.isFetching}
            onClick={() => {
              void balance.refetch();
              void usage.refetch();
            }}
          >
            刷新
          </Button>
        </Space>
      }
    >
      {balance.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="预算余额加载失败"
          description={balance.error instanceof ApiError ? balance.error.friendlyMessage : String(balance.error)}
        />
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Statistic title={`${budgetYear} 年度额度`} value={summary?.budgetAmount ?? 0} precision={2} />
        </Col>
        <Col span={5}>
          <Statistic title="已用" value={summary?.usedAmount ?? 0} precision={2} />
        </Col>
        <Col span={5}>
          <Statistic title="冻结（申领预占）" value={summary?.frozenAmount ?? 0} precision={2} />
        </Col>
        <Col span={5}>
          <Statistic
            title="可用"
            value={summary?.availableAmount ?? 0}
            precision={2}
            valueStyle={{ color: (summary?.availableAmount ?? 0) < 0 ? '#cf1322' : undefined }}
          />
        </Col>
        <Col span={4}>
          <Statistic
            title="执行率"
            value={(summary?.execRate ?? 0) * 100}
            precision={1}
            suffix="%"
            valueStyle={{ color: summary?.overBudget ? '#cf1322' : undefined }}
          />
        </Col>
      </Row>

      {summary?.note ? (
        <Typography.Paragraph type="secondary">{summary.note}</Typography.Paragraph>
      ) : null}

      <Space style={{ marginBottom: 12 }} wrap>
        <Typography.Text strong>科室预算执行</Typography.Text>
        <Typography.Text type="secondary">
          <Switch size="small" checked={overBudgetOnly} onChange={setOverBudgetOnly} /> 只看超预算流水
        </Typography.Text>
      </Space>

      <Table
        rowKey="deptId"
        size="middle"
        pagination={false}
        style={{ marginBottom: 16 }}
        dataSource={summary?.depts ?? []}
        scroll={{ x: 760 }}
        columns={[
          { title: '科室', dataIndex: 'deptName', width: 180, render: (value: string | null) => value || '-' },
          { title: '额度', dataIndex: 'budgetAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
          { title: '已用', dataIndex: 'usedAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
          { title: '冻结', dataIndex: 'frozenAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
          {
            title: '可用',
            dataIndex: 'availableAmount',
            width: 130,
            align: 'right',
            render: (value: number) => (
              <Typography.Text type={value < 0 ? 'danger' : undefined}>{formatAmount(value)}</Typography.Text>
            )
          },
          {
            title: '执行率',
            dataIndex: 'execRate',
            width: 100,
            align: 'right',
            render: (value: number) => `${formatQty((value ?? 0) * 100)}%`
          },
          {
            title: '超预算',
            dataIndex: 'overBudget',
            width: 90,
            render: (value: boolean) => (value ? <Tag color="red">是</Tag> : '-')
          }
        ]}
      />

      {usage.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="预算流水加载失败"
          description={usage.error instanceof ApiError ? usage.error.friendlyMessage : String(usage.error)}
        />
      ) : null}

      <Table<BudgetUsageRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={usage.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无预算流水' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {usage.hasNextPage ? (
          <Button onClick={() => usage.fetchNextPage()} loading={usage.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
